/**
 * cypAdhdStages.js
 *
 * Single source of truth for CYP ADHD assessment stage metadata on the
 * frontend. Mirrors adultAutismStages.js but adapted for the CYP ADHD
 * pathway which has four stages (no Behavioural Observation) and uses
 * 'clinicalInterview' as its CI stage key rather than 'history'.
 *
 * CYP ADHD stages:
 *   clinicalInterview        — dual-column parent/school developmental history
 *   semiStructuredInterview  — SNAP-IV + clinician notes
 *   diagnosticDecision       — MDT review / diagnostic formulation
 *   postAssessmentDiscussion — feedback session with parent/carer
 *
 * Must stay aligned with:
 *   - python-functions/functions/cyp_adhd_patient_builder.py
 *     (ASSESSMENT_STAGE_KEYS)
 *   - python-functions/functions/cyp_adhd_stage_progress.py
 *     (STAGE_TO_PROGRESS_KEY, REPORT_APPROVAL_FIELDS)
 */

// ── Stage keys ──────────────────────────────────────────────────────────────
// Canonical Firestore document IDs. Order matches clinical workflow.
// No optional stages in CYP ADHD v1 (Observation is not part of the pathway).

export const CYP_ADHD_STAGE_KEYS = [
  'clinicalInterview',
  'semiStructuredInterview',
  'diagnosticDecision',
  'postAssessmentDiscussion',
];

// ── Report-field prefix map ─────────────────────────────────────────────────
// Maps each stage key to the abbreviated prefix used in Firestore field names
// for report-level flags. CI uses 'ci' (not 'history') to distinguish from
// the autism pathway.

export const STAGE_TO_REPORT_PREFIX = {
  clinicalInterview: 'ci',
  semiStructuredInterview: 'ssi',
  diagnosticDecision: 'dd',
  postAssessmentDiscussion: 'pad',
  // The parent-facing Patient Report (cyp_car registry key 'patientReport')
  // and the SNAP-IV appendix ('ratingScales'); neither is an assessment stage
  // — their reportContent docs carry only the render processing status.
  patientReport: 'patientReport',
  ratingScales: 'ratingScales',
};

// ── Report stage labels ─────────────────────────────────────────────────────
// The `reportStage` string GenerateDraftWithLLM uses to route to the CYP
// backend processors.
//
// ONE map, deliberately. There are two entry points into CYP generation —
// generating on stage exit (useModularSectionExitRegeneration) and the
// Regenerate button (TriggerRegenerateCypAdhdStage) — and they used to keep
// private copies of this. The copies drifted: the SSI pipeline shipped, the
// stage-exit copy gained it, the regenerate copy did not, and the Regenerate
// button failed its parameter guard and told the clinician to check their
// internet connection. Both now import this.
// The parent-facing Patient Report's reportStage string — the embed and
// download CFs resolve it to documents.cypAdhdPatientReport. A dedicated
// constant (not an entry in STAGE_KEY_TO_CYP_REPORT_STAGE below) because that
// map routes LLM DRAFT generation, which the patient report does not have —
// it renders from the other stages' approved outputs.
export const CYP_PATIENT_REPORT_STAGE = 'CYP ADHD Patient Report';

export const STAGE_KEY_TO_CYP_REPORT_STAGE = {
  clinicalInterview: 'CYP ADHD Clinical Interview',
  semiStructuredInterview: 'CYP ADHD Semi-Structured Interview',
  diagnosticDecision: 'CYP ADHD Diagnostic Decision',
  postAssessmentDiscussion: 'CYP ADHD Post-Assessment Discussion',
};

// ── Dashboard progress key map ──────────────────────────────────────────────
// CI and SSI are separate pills, matching the adult ADHD dashboard row. The
// original combined 'ddi' pill was autism's pattern; CYP's CI and SSI are
// distinct stages with distinct reports, so one pill hid which had stalled.
// Mirrors STAGE_TO_PROGRESS_KEY in python-functions/functions/
// cyp_adhd_stage_progress.py, which computes the values these keys read.

export const STAGE_TO_PROGRESS_KEY = {
  clinicalInterview: 'ci',
  semiStructuredInterview: 'ssi',
  diagnosticDecision: 'dd',
  postAssessmentDiscussion: 'pad',
};

// ── Clinician role map ──────────────────────────────────────────────────────
// Maps each stage to the clinician role field on the patient header document.

export const STAGE_TO_CLINICIAN_KEY = {
  clinicalInterview: 'clinicianCI',
  semiStructuredInterview: 'clinicianSSI',
  diagnosticDecision: 'clinicianDD',
  postAssessmentDiscussion: 'clinicianPAD',
};

// ── Flag-name helpers ───────────────────────────────────────────────────────

/**
 * Firestore field name for the report-approved flag on a reportContent
 * document. e.g. 'clinicalInterview' -> 'ciReportApproved'.
 */
export function getReportApprovedField(stageKey) {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  if (!prefix) throw new Error(`Unknown CYP ADHD stage key: ${stageKey}`);
  return `${prefix}ReportApproved`;
}

/**
 * Firestore field name for the report-generated flag on a reportContent
 * document. e.g. 'semiStructuredInterview' -> 'ssiReportGenerated'.
 */
export function getReportGeneratedField(stageKey) {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  if (!prefix) throw new Error(`Unknown CYP ADHD stage key: ${stageKey}`);
  return `${prefix}ReportGenerated`;
}
